// src/components/store/sections/navbar/StoreAppDownloadBar.tsx
"use client";

import React, { useState, useEffect, useCallback } from "react";
import {
  Smartphone,
  Apple,
  Monitor,
  Download,
  Sparkles,
  X,
  ShieldCheck,
  Layers,
} from "lucide-react";

export interface StoreAppDownloadBarProps {
  subdomain?: string;
  storeName?: string;
  primaryColor?: string;
  secondaryColor?: string;
  appDistribution?: any;
}

export default function StoreAppDownloadBar({
  subdomain = "nextlook",
  storeName = "Store",
  primaryColor = "#059669",
  secondaryColor = "#047857",
  appDistribution: propAppDistribution,
}: StoreAppDownloadBarProps) {
  const [appData, setAppData] = useState<any>(propAppDistribution || null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const getFallbackDownloadUrl = useCallback(
    (plat: string) => {
      if (typeof window !== "undefined") {
        const isLocal =
          window.location.hostname.includes("localhost") ||
          window.location.hostname === "127.0.0.1";
        const base = isLocal ? `http://${subdomain}.localhost:8000` : "";
        return `${base}/api/v1/stores/app-builder/download/${plat}/?subdomain=${encodeURIComponent(
          subdomain
        )}`;
      }
      return `/api/v1/stores/app-builder/download/${plat}/?subdomain=${encodeURIComponent(
        subdomain
      )}`;
    },
    [subdomain]
  );

  const loadAppDistribution = useCallback(async () => {
    if (propAppDistribution && Object.keys(propAppDistribution).length > 0) {
      setAppData(propAppDistribution);
    }

    if (typeof window !== "undefined") {
      try {
        const localRaw =
          localStorage.getItem(`store_app_distribution_${subdomain}`) ||
          localStorage.getItem("store_app_distribution");
        if (localRaw) {
          const parsed = JSON.parse(localRaw);
          if (parsed && typeof parsed === "object") {
            setAppData((prev: any) => ({ ...prev, ...parsed }));
          }
        }
      } catch {}

      try {
        const isLocal =
          window.location.hostname.includes("localhost") ||
          window.location.hostname === "127.0.0.1";
        const apiBase = isLocal ? `http://${subdomain}.localhost:8000` : "";
        const res = await fetch(
          `${apiBase}/api/v1/stores/app-builder/?subdomain=${encodeURIComponent(subdomain)}`,
          {
            headers: {
              Accept: "application/json",
              "X-Tenant-Subdomain": subdomain,
            },
          }
        );
        if (res.ok) {
          const data = await res.json();
          if (data.app_config) {
            setAppData(data.app_config);
          }
        }
      } catch {}
    }
  }, [subdomain, propAppDistribution]);

  useEffect(() => {
    loadAppDistribution();

    const handleUpdate = (e: any) => {
      if (e?.detail) {
        setAppData((prev: any) => ({ ...prev, ...e.detail }));
      } else {
        loadAppDistribution();
      }
    };

    const handleOpenModal = () => setIsModalOpen(true);

    if (typeof window !== "undefined") {
      window.addEventListener("app_distribution_updated", handleUpdate);
      window.addEventListener("storeSettingsUpdated", handleUpdate);
      window.addEventListener("storage", handleUpdate);
      window.addEventListener("openStoreAppModal", handleOpenModal);
      return () => {
        window.removeEventListener("app_distribution_updated", handleUpdate);
        window.removeEventListener("storeSettingsUpdated", handleUpdate);
        window.removeEventListener("storage", handleUpdate);
        window.removeEventListener("openStoreAppModal", handleOpenModal);
      };
    }
  }, [loadAppDistribution]);

  if (appData && appData.show_apps_in_navbar === false) {
    return null;
  }

  const appName = appData?.app_name || `${storeName} App`;
  const appSlogan = appData?.app_slogan || "Fast & Zero-Friction Mobile Experience";
  const badgeText = appData?.navbar_badge_text || "Download App";
  const brandColor = appData?.primary_color || primaryColor || "#059669";

  const apkUrl =
    appData?.apk_download_url ||
    appData?.store_apk_url ||
    getFallbackDownloadUrl("apk");
  const iosUrl =
    appData?.ios_download_url ||
    appData?.store_ios_url ||
    getFallbackDownloadUrl("ios");
  const winUrl =
    appData?.windows_download_url ||
    appData?.store_windows_url ||
    getFallbackDownloadUrl("windows");

  const adminApkUrl =
    appData?.admin_apk_download_url ||
    appData?.admin_apk_url ||
    getFallbackDownloadUrl("admin_apk");
  const adminIosUrl =
    appData?.admin_ios_download_url ||
    appData?.admin_ios_url ||
    getFallbackDownloadUrl("admin_ios");
  const adminWinUrl =
    appData?.admin_windows_download_url ||
    appData?.admin_windows_url ||
    getFallbackDownloadUrl("admin_windows");
  const showAdminApps = Boolean(appData?.show_admin_apps_in_navbar);

  return (
    <>
      {/* 🌟 Top Integrated Storefront Navbar App Download Strip */}
      <div className="hidden md:block w-full bg-slate-950 text-white border-b border-white/10 px-3 sm:px-6 py-1.5 text-xs select-none font-sans">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <button
              type="button"
              onClick={() => setIsModalOpen(true)}
              style={{ backgroundColor: brandColor }}
              className="px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider text-white flex items-center gap-1 shrink-0 shadow-xs cursor-pointer"
            >
              <Sparkles className="w-2.5 h-2.5" />
              <span>{badgeText}</span>
            </button>

            <span className="font-bold text-[11px] text-white/90 truncate">
              {appName}
            </span>
            <span className="hidden md:inline text-[10px] text-slate-400 truncate">
              — {appSlogan}
            </span>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2">
            {/* 1. Android APK Button */}
            <a
              href={apkUrl}
              target="_blank"
              rel="noreferrer"
              className="px-2.5 py-1 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/30 text-emerald-300 font-black text-[10px] flex items-center gap-1 transition-all active:scale-95 cursor-pointer"
              title="Download Android APK"
            >
              <Smartphone className="w-3 h-3 text-emerald-400 shrink-0" />
              <span>APK</span>
              <Download className="w-2.5 h-2.5 opacity-80" />
            </a>

            {/* 2. Apple iOS Button */}
            <a
              href={iosUrl}
              target="_blank"
              rel="noreferrer"
              className="px-2.5 py-1 rounded-lg bg-sky-500/15 hover:bg-sky-500/25 border border-sky-500/30 text-sky-300 font-black text-[10px] flex items-center gap-1 transition-all active:scale-95 cursor-pointer"
              title="Download iOS App"
            >
              <Apple className="w-3 h-3 text-sky-400 shrink-0" />
              <span>iOS</span>
              <Download className="w-2.5 h-2.5 opacity-80" />
            </a>

            {/* 3. Windows .EXE Button */}
            <a
              href={winUrl}
              target="_blank"
              rel="noreferrer"
              className="px-2.5 py-1 rounded-lg bg-indigo-500/15 hover:bg-indigo-500/25 border border-indigo-500/30 text-indigo-300 font-black text-[10px] flex items-center gap-1 transition-all active:scale-95 cursor-pointer"
              title="Download Windows Desktop App (.exe)"
            >
              <Monitor className="w-3 h-3 text-indigo-400 shrink-0" />
              <span>Windows</span>
              <Download className="w-2.5 h-2.5 opacity-80" />
            </a>

            {showAdminApps && (
              <button
                type="button"
                onClick={() => setIsModalOpen(true)}
                className="hidden sm:flex px-2.5 py-1 rounded-lg bg-purple-500/15 hover:bg-purple-500/25 border border-purple-500/30 text-purple-300 font-black text-[10px] items-center gap-1 transition-all cursor-pointer"
              >
                <Layers className="w-3 h-3 text-purple-400" />
                <span>Admin POS</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* 🌟 App Download Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-slate-950/75 backdrop-blur-md animate-in fade-in select-none font-sans">
          <div className="w-full max-w-lg rounded-3xl bg-slate-900 border-2 border-slate-800 text-white p-6 shadow-2xl space-y-5 relative overflow-hidden">
            <div
              className="absolute -top-16 -right-16 w-44 h-44 rounded-full blur-3xl opacity-20 pointer-events-none"
              style={{ backgroundColor: brandColor }}
            />

            <div className="flex items-start justify-between gap-3 relative z-10">
              <div className="flex items-center gap-3">
                {appData?.app_icon_url ? (
                  <img
                    src={appData.app_icon_url}
                    alt={appName}
                    className="w-12 h-12 rounded-2xl object-cover border border-white/15 shadow-md"
                  />
                ) : (
                  <div
                    style={{ backgroundColor: brandColor }}
                    className="w-12 h-12 rounded-2xl flex items-center justify-center text-white font-black text-xl shadow-lg"
                  >
                    {appName.charAt(0).toUpperCase()}
                  </div>
                )}
                <div>
                  <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-400 font-black block">
                    OFFICIAL WHITE-LABEL APPS (V{appData?.app_version || "1.0.0"})
                  </span>
                  <h3 className="text-lg font-black text-white">{appName}</h3>
                  <p className="text-xs text-slate-400">{appSlogan}</p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="p-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-2.5 relative z-10">
              <div className="text-[10px] font-mono font-black uppercase tracking-wider text-slate-400">
                Customer Storefront App (APK, iOS & Windows)
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                <a
                  href={apkUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="p-3.5 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/20 flex flex-col items-center text-center gap-1.5 transition-all"
                >
                  <Smartphone className="w-6 h-6 text-emerald-400" />
                  <span className="text-xs font-black text-white">Android APK</span>
                  <span className="text-[10px] text-emerald-300 font-mono flex items-center gap-1">
                    <Download className="w-3 h-3" /> Download APK
                  </span>
                </a>

                <a
                  href={iosUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="p-3.5 rounded-2xl border border-sky-500/30 bg-sky-500/10 hover:bg-sky-500/20 flex flex-col items-center text-center gap-1.5 transition-all"
                >
                  <Apple className="w-6 h-6 text-sky-400" />
                  <span className="text-xs font-black text-white">Apple iOS</span>
                  <span className="text-[10px] text-sky-300 font-mono flex items-center gap-1">
                    <Download className="w-3 h-3" /> Download iOS
                  </span>
                </a>

                <a
                  href={winUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="p-3.5 rounded-2xl border border-indigo-500/30 bg-indigo-500/10 hover:bg-indigo-500/20 flex flex-col items-center text-center gap-1.5 transition-all"
                >
                  <Monitor className="w-6 h-6 text-indigo-400" />
                  <span className="text-xs font-black text-white">Windows .EXE</span>
                  <span className="text-[10px] text-indigo-300 font-mono flex items-center gap-1">
                    <Download className="w-3 h-3" /> Download .EXE
                  </span>
                </a>
              </div>
            </div>

            {showAdminApps && (
              <div className="space-y-2.5 pt-3 border-t border-slate-800 relative z-10">
                <div className="text-[10px] font-mono font-black uppercase tracking-wider text-purple-400">
                  Merchant Admin & POS Client (APK, iOS & Windows)
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <a
                    href={adminApkUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="p-2.5 rounded-xl border border-slate-800 bg-slate-950/60 hover:border-purple-500/40 text-center text-[11px] font-bold text-slate-300"
                  >
                    Admin APK
                  </a>
                  <a
                    href={adminIosUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="p-2.5 rounded-xl border border-slate-800 bg-slate-950/60 hover:border-purple-500/40 text-center text-[11px] font-bold text-slate-300"
                  >
                    Admin iOS
                  </a>
                  <a
                    href={adminWinUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="p-2.5 rounded-xl border border-slate-800 bg-slate-950/60 hover:border-purple-500/40 text-center text-[11px] font-bold text-slate-300"
                  >
                    Admin Windows
                  </a>
                </div>
              </div>
            )}

            <div className="pt-2 border-t border-slate-800 flex items-center justify-between text-[11px] text-slate-400">
              <span className="flex items-center gap-1 text-emerald-400 font-bold">
                <ShieldCheck className="w-4 h-4" /> Signed & Verified Multi-Tenant Build
              </span>
              <span>Domain: {subdomain}</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
